<template>
  <q-card v-if="loading" flat bordered>
    <div class="text-primary q-pa-md flex justify-between">
      <q-skeleton style="width: 150px; height: 32px" class="q-my-auto" />
      <div class="flex q-gutter-x-sm q-my-auto" style="height: 20px">
        <q-skeleton style="width: 150px" />
      </div>
    </div>
    <q-markup-table class="full-width" flat dense>
      <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
        <tr>
          <th scope="col">
            <q-skeleton animation="blink" type="text" />
          </th>
          <th style="width: 20.7%" scope="col">
            <q-skeleton animation="blink" type="text" />
          </th>
          <th style="width: 22.5%" scope="col">
            <q-skeleton animation="blink" type="text" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="n in 2" :key="n" style="height: 45px">
          <td>
            <q-skeleton animation="blink" />
          </td>
          <td style="width: 244px">
            <q-skeleton animation="blink" />
          </td>
          <td style="width: 266px">
            <q-skeleton animation="blink" />
          </td>
        </tr>
        <tr v-for="n in 3" :key="n" style="height: 45px">
          <td colspan="2">
            <q-skeleton animation="blink" />
          </td>
          <td>
            <q-skeleton animation="blink" />
          </td>
        </tr>
      </tbody>
    </q-markup-table>
  </q-card>
  <q-card v-else flat bordered>
    <div class="q-pa-md flex justify-between">
      <div class="text-h6">Upcoming Invoice</div>
      <div class="q-gutter-x-sm row">
        <q-btn icon="refresh" round flat unelevated size="sm" @click="refreshInvoice" />
        <div class="text-bold q-my-auto">From {{ formatedFromDate }} to {{ formatedToDate }}</div>
      </div>
    </div>
    <q-table
      color="primary"
      :rows="formatedLineItems"
      :columns="columns"
      :pagination="{
        rowsPerPage: 10,
        sortBy: 'Service'
      }"
      hide-bottom
      row-key="name"
      bordered
      dense
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #body="props">
        <q-tr :props="props" style="height: 45px">
          <q-td v-for="col in props.cols" :key="col.name" :props="props">
            {{ col.value }}
          </q-td>
        </q-tr>
      </template>
      <template #bottom-row="props">
        <q-tr style="height: 45px" :props="props">
          <q-td class="text-left text-bold">Subtotal</q-td>
          <q-td></q-td>
          <q-td class="text-right text-bold">{{ subtotalAmount }}</q-td>
        </q-tr>
        <q-tr v-if="discount" style="height: 45px" :props="props">
          <q-td class="text-left">{{ discount.name }}</q-td>
          <q-td></q-td>
          <q-td class="text-right">-{{ formatAmount(discount.amount) }}</q-td>
        </q-tr>
        <q-tr style="height: 45px" :props="props">
          <q-td class="text-left text-bold">Total</q-td>
          <q-td></q-td>
          <q-td class="text-right text-bold">{{ totalAmount }}</q-td>
        </q-tr>
        <q-tr style="height: 45px" :props="props">
          <q-td class="text-left text-bold">Amount Due</q-td>
          <q-td></q-td>
          <q-td class="text-right text-bold">{{ amountDue }}</q-td>
        </q-tr>
      </template>
    </q-table>
  </q-card>
</template>

<script lang="ts">
import { useStripeStore } from '@/stores/stripe'
import type { QTableColumn } from 'quasar'
import { format } from 'date-fns'
import { formatMoney } from '@/utils'

export default {
  data() {
    return {
      columns: [
        { name: 'Service', label: 'Service', field: 'nickname', align: 'left', sortable: true },
        { name: 'Usage', label: 'Usage', field: 'quantity', align: 'left', sortable: true },
        { name: 'Amount', label: 'Amount', field: 'amount', align: 'right', sortable: true }
      ] as QTableColumn[],
      loading: false
    }
  },
  computed: {
    formatedLineItems() {
      const prepareNickname = (description: string | null, amount: number) => {
        if (description === null) {
          return ''
        }
        const base = description.split(' × ')[1]
        if (amount) {
          return base
        }
        const prefix = description.split(' × ')[0].split(' ').slice(1)
        let unit = 'month'
        if (prefix.length) {
          unit = prefix.join(' ')
        }
        return base.replace('month', unit)
      }

      return this.lines.map((line) => ({
        ...line,
        nickname: prepareNickname(line.description, line.amount),
        amount: this.formatAmount(line.amount)
      }))
    },
    formatedFromDate() {
      if (!this.invoice) {
        return ''
      }
      return this.formatDate(this.invoice.period_start)
    },
    formatedToDate() {
      if (!this.invoice) {
        return ''
      }
      return this.formatDate(this.invoice.period_end)
    },
    lines() {
      return this.invoice ? this.invoice.lines : []
    },
    totalAmount() {
      return this.invoice ? this.formatAmount(this.invoice.total) : 0
    },
    subtotalAmount() {
      return this.invoice ? this.formatAmount(this.invoice.subtotal) : 0
    },
    discount() {
      return this.invoice ? this.invoice.discount : null
    },
    amountDue() {
      return this.invoice ? this.formatAmount(this.invoice.amount_due) : null
    },
    invoice() {
      return useStripeStore().upcomingInvoice
    }
  },
  async created() {
    if (!this.invoice) {
      this.refreshInvoice()
    }
  },
  methods: {
    refreshInvoice() {
      this.loading = true
      useStripeStore()
        .initUpcomingInvoice()
        .then(() => {
          this.loading = false
        })
    },
    formatAmount(amount: number) {
      return formatMoney(amount / 100, 2)
    },
    formatDate(date: number) {
      return format(new Date(date * 1000), 'M/d/yyyy')
    },
    totalQuantity(lines: { quantity: number }[]) {
      return lines.reduce((total, line) => total + (line.quantity || 0), 0)
    }
  }
}
</script>
